/**
 * @fileoverview useScrollAnimation Hook - Scroll-triggered Animation Handler
 *
 * Custom Hook für Intersection Observer basierte Scroll-Animationen.
 * Wird für Landing Page Feature-Sektionen verwendet.
 *
 * @module hooks/useScrollAnimation
 */

import { useEffect, useRef, useState, useCallback } from "react";

interface UseScrollAnimationOptions {
  /** Threshold für Intersection Observer (0-1) */
  threshold?: number;
  /** Root Margin für frühere/spätere Trigger */
  rootMargin?: string;
  /** Animation nur einmal triggern */
  triggerOnce?: boolean;
}

interface UseScrollAnimationReturn<T extends HTMLElement = HTMLElement> {
  /** Ref für das zu beobachtende Element */
  ref: React.RefObject<T | null>;
  /** Ob Element sichtbar ist */
  isVisible: boolean;
  /** Manuelles Zurücksetzen der Sichtbarkeit */
  reset: () => void;
}

/**
 * Hook für Scroll-triggered Animationen
 *
 * @param options - Konfiguration für Intersection Observer
 * @returns Ref und Sichtbarkeitsstatus
 *
 * @example
 * ```tsx
 * const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
 *
 * return (
 *   <section
 *     ref={ref}
 *     className={cn(
 *       "transition-all duration-700",
 *       isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
 *     )}
 *   >
 *     Content
 *   </section>
 * );
 * ```
 */
export function useScrollAnimation<T extends HTMLElement = HTMLElement>(
  options: UseScrollAnimationOptions = {}
): UseScrollAnimationReturn<T> {
  const { threshold = 0.1, rootMargin = "0px", triggerOnce = true } = options;

  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const reset = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          // Disconnect nach erstem Trigger wenn triggerOnce aktiv
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible, reset };
}

/**
 * Hook für gestaffelte Animationen mehrerer Elemente
 *
 * @param itemCount - Anzahl der Elemente
 * @param options - Konfiguration
 * @returns Array von Visibility States und Container Ref
 */
export function useStaggeredAnimation<T extends HTMLElement = HTMLElement>(
  itemCount: number,
  options: UseScrollAnimationOptions & { staggerDelay?: number } = {}
): { ref: React.RefObject<T | null>; isVisible: boolean; visibleItems: boolean[] } {
  const { staggerDelay = 100, ...observerOptions } = options;
  const { ref, isVisible } = useScrollAnimation<T>(observerOptions);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(itemCount).fill(false));

  useEffect(() => {
    if (!isVisible) {
      setVisibleItems(new Array(itemCount).fill(false));
      return;
    }

    // Gestaffelte Animation
    const timeouts: NodeJS.Timeout[] = [];

    for (let i = 0; i < itemCount; i++) {
      const timeout = setTimeout(() => {
        setVisibleItems((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, i * staggerDelay);

      timeouts.push(timeout);
    }

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [isVisible, itemCount, staggerDelay]);

  return { ref, isVisible, visibleItems };
}
